import { type FormLayoutProps } from "skedyul-ui/src/components/ui/FormLayout";
import { type ButtonProps } from "skedyul-ui/src/components/ui/Button";
import { type AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

export type DialogSchemaProps = {
  sections: {
    hidden?: boolean;
    actions?: ButtonProps[];
    label: string;
    noPadding?: boolean;
    className?: string;
    fields: FormLayoutProps["fields"];
  }[];
  title: string;
  description: string;
  actions: ButtonProps[];
  error?: string;
  size?: "sm" | "md" | "lg" | "xl";
};

export const getDialogParams = (
  state: Record<string, unknown>,
  params: Record<string, unknown>,
) => {
  return {
    ...state,
    ...params,
  };
};

// Use a map of schema names to import functions
export const SCHEMA_MAP = {
  NEW_WORKPLACE: () =>
    import("./dialogs/newWorkplaceDialog").then((m) => m.newWorkplaceSchema),
  ADD_EVENT_LISTENER: () =>
    import("./dialogs/addEventListenerDialog").then(
      (m) => m.addEventListenerDialog,
    ),
  ADD_ACTION: () =>
    import("./dialogs/addActionDialog").then((m) => m.addActionDialog),
  EDIT_ACTION: () =>
    import("./dialogs/addActionDialog").then((m) => m.addActionDialog),
  SIMPLE_MESSAGE: () =>
    import("./dialogs/simpleMessage").then((m) => m.simpleMessage),
  VIEW_CONTENT: () =>
    import("./dialogs/viewContentDialog").then((m) => m.viewContentDialog),
  APP_INSTALL_CONFIRMATION: () =>
    import("./dialogs/appInstallConfirmation").then(
      (m) => m.appInstallConfirmation,
    ),
  UPLOAD_WORKFLOW_FILE: () =>
    import("./dialogs/uploadWorkflowFileDialog").then(
      (m) => m.uploadWorkflowFileDialog,
    ),
  IMPORT_CSV: () =>
    import("./dialogs/importCSVDialog").then((m) => m.importCSVDialog),
  CREATE_RELATIONSHIP: () =>
    import("./dialogs/createRelationship").then(
      (m) => m.createRelationshipDialog,
    ),
  EDIT_DEFINITION_OPTION: () =>
    import("./dialogs/editDefinitionOption").then(
      (m) => m.editDefinitionOption,
    ),
  CHANGE_FIELD_VALUE: () =>
    import("./dialogs/changeFieldValue").then((m) => m.changeFieldValue),
  CHANGE_DEFAULT_VALUE: () =>
    import("./dialogs/changeDefaultValue").then((m) => m.changeDefaultValue),
  DELETE_FIELD_CONFIRMATION: () =>
    import("./dialogs/deleteFieldConfirmationDialog").then(
      (m) => m.deleteFieldConfirmationDialog,
    ),
  DELETE_MODEL_CONFIRMATION: () =>
    import("./dialogs/deleteModelConfirmationDialog").then(
      (m) => m.deleteModelConfirmationDialog,
    ),
  DELETE_INSTANCE_CONFIRMATION: () =>
    import("./dialogs/deleteInstanceConfirmationDialog").then(
      (m) => m.deleteInstanceConfirmationDialog,
    ),
  BULK_EDIT_FIELD: () =>
    import("./dialogs/bulkEditFieldDialog").then((m) => m.bulkEditFieldDialog),
  DELETE_RELATIONSHIP_CONFIRMATION: () =>
    import("./dialogs/deleteRelationshipConfirmationDialog").then(
      (m) => m.deleteRelationshipConfirmationDialog,
    ),
  ADD_BUILT_IN_DEFINITION: () =>
    import("./dialogs/addBuiltInDefinition").then(
      (m) => m.addBuiltInDefinition,
    ),
  CREATE_CONTACT: () =>
    import("./dialogs/createContact").then((m) => m.createContactDialog),
  RUN_ACTION: () =>
    import("./dialogs/runActionDialog.ts").then((m) => m.runActionDialog),
  ADD_MODEL_ASSOCIATION: () =>
    import("./dialogs/addModelAssociationDialog").then(
      (m) => m.addModelAssociationDialog,
    ),
  ADD_ASSOCIATION_RULE: () =>
    import("./dialogs/editAssociationRuleDialog").then(
      (m) => m.editAssociationRuleDialog,
    ),
  UNINSTALL_APP_CONFIRMATION: () =>
    import("./dialogs/uninstallAppConfirmationDialog").then(
      (m) => m.uninstallAppConfirmationDialog,
    ),
  CREATE_APP: () =>
    import("./dialogs/createApp").then((m) => m.createAppDialog),
  APP_ENV_VARIABLE: () =>
    import("./dialogs/appEnvironmentVariable").then(
      (m) => m.appEnvironmentVariableDialog,
    ),
  INSTALL_ENV_VARIABLE: () =>
    import("./dialogs/installEnvVariable").then(
      (m) => m.installEnvVariableDialog,
    ),
  CONNECT_GITHUB_REPOSITORY: () =>
    import("./dialogs/connectGithubRepoDialog").then(
      (m) => m.connectGithubRepoDialog,
    ),
  SELECT_EXECUTABLE: () =>
    import("./dialogs/selectExecutableDialog").then(
      (m) => m.selectExecutableDialog,
    ),
  TOGGLE_PUBLISH: () =>
    import("./dialogs/togglePublishDialog").then((m) => m.togglePublishDialog),
  CREATE_AGENT: () =>
    import("./dialogs/createAgentDialog").then((m) => m.createAgentDialog),
  ADD_TOOL_BINDING: () =>
    import("./dialogs/addToolBindingDialog").then(
      (m) => m.addToolBindingDialog,
    ),
  OPTIMIZE_AGENT: () =>
    import("./dialogs/optimizeAgentDialog").then((m) => m.optimizeAgentDialog),
  CREATE_INTERNAL_MODEL_INSTANCE: () =>
    import("./dialogs/createInternalModelInstance").then(
      (m) => m.createInternalModelInstanceDialog,
    ),
  FORM_FIELD_MODAL: () =>
    import("./dialogs/formFieldModal").then((m) => m.formFieldModal),
  GRANT_APP_ACCESS: () =>
    import("./dialogs/grantAppAccessDialog").then(
      (m) => m.grantAppAccessDialog,
    ),
  REVOKE_APP_ACCESS: () =>
    import("./dialogs/revokeAppAccessDialog").then(
      (m) => m.revokeAppAccessDialog,
    ),
  MIGRATION_IMPACT: () =>
    import("./dialogs/migrationImpactDialog").then(
      (m) => m.migrationImpactDialog,
    ),
  MIGRATION_DETAIL: () =>
    import("./dialogs/migrationDetailDialog").then(
      (m) => m.migrationDetailDialog,
    ),
  APP_MODEL_RESOURCE_INSTANCE_MAPPER: () =>
    import("./dialogs/appModelResourceInstanceMapper").then(
      (m) => m.appModelResourceInstanceMapperDialog,
    ),
  UPLOAD_REPORT_TEMPLATE: () =>
    import("./dialogs/uploadReportTemplateDialog").then(
      (m) => m.uploadReportTemplateDialog,
    ),
  GENERATE_REPORT: () =>
    import("./dialogs/generateReportDialog").then(
      (m) => m.generateReportDialog,
    ),
  UPDATE_REPORT_TEMPLATE: () =>
    import("./dialogs/updateReportTemplateDialog").then(
      (m) => m.updateReportTemplateDialog,
    ),
  RETRY_WORKFLOW: () =>
    import("./dialogs/retryWorkflowDialog").then((m) => m.retryWorkflowDialog),
  INITIAL_CONTEXT: () =>
    import("./dialogs/initialContextDialog").then(
      (m) => m.initialContextDialog,
    ),
  UPLOAD_CRM_SCHEMA: () =>
    import("./dialogs/uploadCRMSchemaDialog").then(
      (m) => m.uploadCRMSchemaDialog,
    ),
  IMPORT_TEAM_WORKSPACE: () =>
    import("./dialogs/uploadTeamWorkspaceDialog").then(
      (m) => m.uploadTeamWorkspaceDialog,
    ),
  EDIT_STAGE: () =>
    import("./dialogs/stageEditDialog").then((m) => m.editStageDialog),
  ADD_STAGE: () =>
    import("./dialogs/stageEditDialog").then((m) => m.addStageDialog),
  ADD_HOOK_TRIGGER: () =>
    import("./dialogs/addHookTriggerDialog").then(
      (m) => m.addHookTriggerDialog,
    ),
  EDIT_HOOK_TRIGGER: () =>
    import("./dialogs/addHookTriggerDialog").then(
      (m) => m.addHookTriggerDialog,
    ),
  AGENT_INPUTS: () =>
    import("./dialogs/agentInputsDialog").then((m) => m.agentInputsDialog),
  ADD_EVENT_SUBSCRIPTION: () =>
    import("./dialogs/addEventSubscriptionDialog").then(
      (m) => m.addEventSubscriptionDialog,
    ),
  EDIT_EVENT_SUBSCRIPTION: () =>
    import("./dialogs/addEventSubscriptionDialog").then(
      (m) => m.addEventSubscriptionDialog,
    ),
  EDIT_TRIGGER_INPUT_MAPPING: () =>
    import("./dialogs/editTriggerInputMappingDialog").then(
      (m) => m.editTriggerInputMappingDialog,
    ),
  FIX_VALIDATION_ERRORS: () =>
    import("./dialogs/fixValidationErrorsDialog").then(
      (m) => m.fixValidationErrorsDialog,
    ),
  EDIT_LIST_VIEW: () =>
    import("./dialogs/editListViewDialog").then((m) => m.editListViewDialog),
  CREATE_LIST_VIEW: () =>
    import("./dialogs/editListViewDialog").then((m) => m.editListViewDialog),
  DELETE_LIST_VIEW: () =>
    import("./dialogs/deleteListViewDialog").then(
      (m) => m.deleteListViewDialog,
    ),
  CREATE_WORKSPACE_PAGE: () =>
    import("./dialogs/createWorkspacePageDialog").then(
      (m) => m.createWorkspacePageDialog,
    ),
  MAKE_PAGE_TEAM_AVAILABLE: () =>
    import("./dialogs/makePageTeamAvailableDialog").then(
      (m) => m.makePageTeamAvailableDialog,
    ),
  CHANGE_LIST_PAGE_MODEL: () =>
    import("./dialogs/changeListPageModelDialog").then(
      (m) => m.changeListPageModelDialog,
    ),
};

// Update the generateDialogFromSchema function to handle async imports
export const generateDialogFromSchema = async <
  P = unknown,
  R = DialogSchemaProps,
>(
  schema: string,
  params: P,
  router: AppRouterInstance,
): Promise<R> => {
  if (schema) {
    try {
      const schemaKey = schema as keyof typeof SCHEMA_MAP;
      if (SCHEMA_MAP[schemaKey]) {
        // Dynamically import the schema module
        const schemaModule = (await SCHEMA_MAP[schemaKey]()) as unknown as (
          p: P,
          r: AppRouterInstance,
        ) => R | Promise<R>;
        // Call the schema function with params and router
        if (typeof params !== "object" || params === null) {
          throw new Error("Dialog params must be an object");
        }
        return schemaModule(params, router);
      }
    } catch (e) {
      console.error(e);

      return {
        sections: [],
        title: "",
        description: "",
        actions: [],
      } as R;
    }
  }

  return {
    sections: [],
    title: "",
    description: "",
    actions: [],
  } as R;
};
